import {
  DraftQuotationPageQueryResult,
  useDeleteDraftQuotationDetailMutation,
  useDeleteDraftQuotationMutation,
  useUpdateDraftQuotationMutation,
  useDraftQuotationPageLazyQuery,
  useWholesalesQuery,
} from 'features/graphql';
import React, { useEffect, useState } from 'react';
import { z } from 'zod';
import { useForm } from 'react-hook-form'; // SubmitHandler は useForm から直接インポートしない
import { zodResolver } from '@hookform/resolvers/zod';
import { Link, useNavigate } from 'react-router-dom';
import { Form, Container, Table, Row, Col, Alert, Button } from 'react-bootstrap';
import { ConfirmModal } from 'components/ConfirmModal';
import { NoteModal } from './NoteModal';
import { formatDate, graphQlErrorMessage, parseISODateAndformatDate } from 'utils/miscellaneous';
import { formatPrice } from 'utils/priceUtils';
import { WholesaleDealPriceCells } from 'components/WholesaleDealPriceCells';
import { WholesaleNameHeaderCells } from 'components/WholesaleNameHeaderCells';
import { Loading } from 'components/Loading';
import { SubmitQuotationModal } from './SubmitQuotationModal';
import { MEDICINES_PATH } from 'route/paths';
import { Plus } from 'react-bootstrap-icons';
import { FormInput } from 'components/FormInput'; // FormInput コンポーネントをインポート
import { AddMedicine } from 'features/medicine/AddMedicine';

const quotationSchema = z.object({
  note: z.string().nullable(),
  orderStatus: z.string().nullable(),
});

type QuotationSchema = z.infer<typeof quotationSchema>;

export const DraftQuotation = () => {
  const { watch, reset, control, getValues } = useForm<QuotationSchema>({
    resolver: zodResolver(quotationSchema),
    defaultValues: {
      note: '',
      orderStatus: null,
    },
  });

  const [pageData, setPageData] = useState<DraftQuotationPageQueryResult['data'] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();

  const [pageDataQuery, { loading: pageLoading }] = useDraftQuotationPageLazyQuery({
    onCompleted(data) {
      setPageData(data);
      reset({
        note: data.draftQuotation?.note,
        orderStatus: data.draftQuotation?.orderStatus,
      });
    },
    onError(error) {
      setError(graphQlErrorMessage(error) ?? null);
    },
  });

  const [updateDraftQuotationMutation, { loading: updateLoading }] =
    useUpdateDraftQuotationMutation({
      onCompleted({ updateDraftQuotation }) {
        if (pageData && updateDraftQuotation) {
          setPageData({ ...pageData, draftQuotation: updateDraftQuotation });
        }
      },
      onError(error) {
        setError(graphQlErrorMessage(error) ?? null);
      },
    });

  const [deleteQuotationMutation, { loading: deleteLoading }] = useDeleteDraftQuotationMutation({
    onCompleted() {
      setPageData(null);
      navigate('/');
    },
    onError(error) {
      setError(graphQlErrorMessage(error) ?? null);
    },
  });

  const [deleteDraftQuotationDetailMutation, { loading: deleteDetailLoading }] =
    useDeleteDraftQuotationDetailMutation({
      onCompleted({ deleteDraftQuotationDetail }) {
        if (pageData && deleteDraftQuotationDetail) {
          setPageData({ ...pageData, draftQuotation: deleteDraftQuotationDetail });
        }
      },
      onError(error) {
        setError(graphQlErrorMessage(error) ?? null);
      },
    });

  const [showModal, setShowModal] = useState<
    'deleteModal' | 'addDetailModal' | 'deleteDetailModal' | 'noteModal' | 'submitModal' | null
  >(null);
  const [targetDraftQuotationDetailId, setTargetDraftQuotationDetailId] = useState<number>();

  useEffect(() => {
    pageDataQuery();

    const subscription = watch((value, { name, type }) => {
      if (name === 'orderStatus' && type === 'change') {
        updateDraftQuotationMutation({
          variables: {
            orderStatus: value.orderStatus,
            note: getValues('note'), // noteも一緒に更新する場合
          },
        });
      }
    });
    return () => subscription.unsubscribe();
  }, [pageDataQuery, watch, updateDraftQuotationMutation, getValues]);

  const draftDetails = pageData?.draftQuotation?.details ?? [];

  const { data: wholesalesData, loading: wholesalesLoading } = useWholesalesQuery();

  const handleDeleteQuotation = () => {
    deleteQuotationMutation().then((res) => {
      if (res.data) {
        setShowModal(null);
      }
    });
  };

  const handleDeleteDetail = (id: number) => {
    deleteDraftQuotationDetailMutation({ variables: { draftQuotationDetailId: id } }).then(
      (res) => {
        if (res.data) {
          setTargetDraftQuotationDetailId(undefined);
          setShowModal(null);
        }
      }
    );
  };

  const targetQuotationDetail = draftDetails?.find(
    (detail) => detail.draftQuotationDetailId === targetDraftQuotationDetailId
  );

  return (
    <>
      <Container fluid className="mt-5 px-5 pb-5 sheet" style={{ marginBottom: '10rem' }}>
        <Row></Row>
        <Row className="mt-4">
          <Col>
            <div className="fs-3">見積依頼</div>
          </Col>
          <Col></Col>
          <Col className="d-flex justify-content-end">
            <table>
              <tbody>
                <tr>
                  <td style={{ whiteSpace: 'nowrap' }}>依頼日:</td>
                  <td style={{ whiteSpace: 'nowrap' }}>{formatDate(new Date())}</td>
                </tr>
                <tr>
                  <td style={{ whiteSpace: 'nowrap' }}>回答予定日:</td>
                  <td style={{ whiteSpace: 'nowrap' }}>
                    {parseISODateAndformatDate(pageData?.selectBusinessCalender.businessDay)}
                  </td>
                </tr>
              </tbody>
            </table>
          </Col>
        </Row>

        <hr />
        <Row className="mt-2">
          <Col>{error && <Alert variant="danger">{error}</Alert>}</Col>
        </Row>

        <Row className="mt-4">
          <Col className="col-12 d-flex justify-content-center">
            <Link className="btn btn-success me-3" to={MEDICINES_PATH}>
              <Plus className="fs-2 me-5" />
              <span className="me-5">商品を検索</span>
            </Link>
          </Col>
        </Row>

        <Row className="mt-5">
          <div style={{ overflowX: 'auto', width: '100%' }}>
            <Table className="table-borderless">
              <thead className="text-success border-bottom border-success border-3">
                <tr>
                  <th
                    key="no"
                    rowSpan={2}
                    className="align-middle text-nowrap"
                    style={{ width: '3rem' }}
                  >
                    No
                  </th>
                  <th
                    key="category"
                    rowSpan={2}
                    className="align-middle text-nowrap"
                    style={{ width: '3rem' }}
                  >
                    区分
                  </th>
                  <th key="name" style={{ width: '35rem', whiteSpace: 'nowrap' }}>
                    品名
                  </th>
                  <th
                    key="price"
                    rowSpan={2}
                    className="align-middle text-nowrap"
                    style={{ width: '7rem' }}
                  >
                    薬価
                  </th>
                  <WholesaleNameHeaderCells
                    wholesales={wholesalesData?.wholesales}
                    rowSpan={2}
                    loading={wholesalesLoading}
                    getKey={(wholesaleId, index) => `header-${wholesaleId || index}`}
                  />
                  <th key="manufacturer" style={{ width: '15rem', whiteSpace: 'nowrap' }}>
                    販売元名
                  </th>
                  <th key="jan" style={{ width: '10rem', whiteSpace: 'nowrap' }}>
                    JANコード
                  </th>
                  <th key="yj" style={{ width: '10rem', whiteSpace: 'nowrap' }}>
                    YJコード
                  </th>
                  <th key="actions" rowSpan={2} className="text-nowrap"></th>
                </tr>
                <tr>
                  <th key="capacity" style={{ whiteSpace: 'nowrap' }}>
                    規格
                  </th>
                  <th key="empty-manufacturer" style={{ whiteSpace: 'nowrap' }}></th>
                  <th key="gs1" style={{ whiteSpace: 'nowrap' }}>
                    GS1コード
                  </th>
                </tr>
              </thead>
              {draftDetails.map((detail, i) => {
                return (
                  <tbody key={detail.draftQuotationDetailId}>
                    <tr>
                      <td colSpan={7} style={{ border: 'none', padding: 0 }}></td>
                    </tr>
                    <tr>
                      <td rowSpan={2} className="align-middle text-center text-nowrap">
                        {i + 1}
                      </td>
                      <td rowSpan={2} className="align-middle text-nowrap">
                        {detail.medicineType}
                      </td>
                      <td className="text-success" style={{ whiteSpace: 'nowrap' }}>
                        <span>{detail.medicineName}</span>
                      </td>
                      <td rowSpan={3} className="align-middle text-nowrap">
                        {formatPrice(detail.price)}
                      </td>

                      <WholesaleDealPriceCells
                        wholesales={wholesalesData?.wholesales}
                        deals={detail.deals}
                        getKey={(wholesaleId, index) =>
                          `price-${wholesaleId || index}-${detail.draftQuotationDetailId}`
                        }
                      />
                      <td className="border-bottom text-nowrap">{detail.salesManufacturer}</td>
                      <td className="border-bottom text-nowrap">{detail.janCode}</td>
                      <td className="border-bottom text-nowrap">{detail.yjCode}</td>
                      <td
                        rowSpan={3}
                        className="p-0 align-middle text-center"
                        style={{ whiteSpace: 'nowrap', border: 'none' }}
                      >
                        <Button
                          type="button"
                          variant="link"
                          className="btn-close p-0"
                          aria-label="Close"
                          onClick={() => {
                            setError(null);
                            setTargetDraftQuotationDetailId(detail.draftQuotationDetailId);
                            setShowModal('deleteDetailModal');
                          }}
                        />
                      </td>
                    </tr>
                    <tr>
                      <td style={{ whiteSpace: 'nowrap' }}>{detail?.capacity}</td>
                      <td style={{ whiteSpace: 'nowrap' }}></td>
                      <td style={{ whiteSpace: 'nowrap' }}>{detail.gs1Code}</td>
                    </tr>
                    <tr className="border-bottom">
                      <td colSpan={3} style={{ whiteSpace: 'nowrap' }}></td>
                      <td colSpan={3} className="text-secondary text-break text-pre-wrap p-1">
                        {detail.note}
                        <Button
                          variant="outline-secondary"
                          className="ms-2"
                          size="sm"
                          onClick={() => {
                            setError(null);
                            setTargetDraftQuotationDetailId(detail.draftQuotationDetailId);
                            setShowModal('noteModal');
                          }}
                        >
                          {(detail.note?.length ?? 0) ? '編集' : '備考を追加'}
                        </Button>
                      </td>
                    </tr>
                  </tbody>
                );
              })}
              <tfoot className="border-top border-success border-3">
                <tr>
                  <td></td>
                </tr>
              </tfoot>
            </Table>
          </div>
        </Row>

        {0 < draftDetails.length && (
          <>
            <Form>
              {/*<Row className="fs-5 mt-3">*/}
              {/*  <Form.Label className="mb-3">注文分類</Form.Label>*/}
              {/*  <Form.Group className="mb-3">*/}
              {/*    <Form.Check*/}
              {/*      inline*/}
              {/*      value={REGULAR_ORDER}*/}
              {/*      label={REGULAR_ORDER}*/}
              {/*      type="radio"*/}
              {/*      {...register('orderStatus')}*/}
              {/*    />*/}
              {/*    <Form.Check*/}
              {/*      inline*/}
              {/*      value={TEMPORARY_ORDER}*/}
              {/*      label={TEMPORARY_ORDER}*/}
              {/*      type="radio"*/}
              {/*      {...register('orderStatus')}*/}
              {/*    />*/}
              {/*    <Form.Check*/}
              {/*      inline*/}
              {/*      value={GENERIC_SWITCH_ORDER}*/}
              {/*      label={GENERIC_SWITCH_ORDER}*/}
              {/*      type="radio"*/}
              {/*      {...register('orderStatus')}*/}
              {/*    />*/}
              {/*  </Form.Group>*/}
              {/*</Row>*/}

              <Row className="fs-5 mt-3">
                <FormInput
                  label="ご要望など"
                  name="note" // react-hook-form の name と一致させる
                  control={control}
                  as="textarea"
                  placeholder=""
                  style={{
                    height: 'calc(1.5rem * 3)',
                    lineHeight: '1.5',
                  }}
                />
              </Row>
            </Form>

            <Row className="mt-3 mb-5">
              <Col className="col-12 d-flex justify-content-center">
                <Button
                  variant="secondary"
                  className="btn-lg me-3"
                  onClick={() => {
                    setError(null);
                    setShowModal('deleteModal');
                  }}
                >
                  見積破棄
                </Button>
                <Button
                  variant="success"
                  className="btn-lg"
                  onClick={() => {
                    setError(null);
                    setShowModal('submitModal');
                  }}
                >
                  内容確認
                </Button>
              </Col>
            </Row>
          </>
        )}
      </Container>

      <AddMedicine
        show={showModal === 'addDetailModal'}
        handleOk={() => {
          pageDataQuery();
          setShowModal(null);
        }}
        handleCancel={() => setShowModal(null)}
      />

      <ConfirmModal
        show={showModal === 'deleteModal'}
        title="見積破棄"
        message={'現在作成中の見積内容をすべて削除しますか？'}
        error={error}
        handleOK={handleDeleteQuotation}
        handleCancel={() => setShowModal(null)}
      />

      {targetQuotationDetail && (
        <ConfirmModal
          show={showModal === 'deleteDetailModal'}
          title="品名削除"
          message={`${targetQuotationDetail.medicineName || ''}を削除しますか？`}
          error={error}
          handleOK={() => handleDeleteDetail(targetQuotationDetail.draftQuotationDetailId)}
          handleCancel={() => setShowModal(null)}
        />
      )}

      {targetQuotationDetail && (
        <NoteModal
          show={showModal === 'noteModal'}
          title="備考"
          targetQuotationDetail={targetQuotationDetail}
          handleCancel={() => {
            setShowModal(null);
            setTargetDraftQuotationDetailId(undefined);
            pageDataQuery();
          }}
        />
      )}

      <SubmitQuotationModal
        show={showModal === 'submitModal'}
        note={getValues('note') ?? undefined}
        handleOk={() => {
          pageDataQuery();
          setShowModal(null);
          reset();
        }}
        handleCancel={() => {
          setShowModal(null);
        }}
      />

      {(pageLoading ||
        updateLoading ||
        deleteLoading ||
        deleteDetailLoading ||
        wholesalesLoading) && <Loading />}
    </>
  );
};

export default DraftQuotation;
